import React, { FunctionComponent, useEffect, useState } from 'react';

import { useMediaQuery } from 'react-responsive';
import { useLocation, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  SessionState,
  setSessionChecked,
  setUser,
  setProfile,
} from 'features/session/sessionSlice';
import Text from 'common/components/Text/Text';
import ExploreTopic from './ExploreTopic';
import ProfileCard from './ProfileCard';
import {
  setApplicationStarted,
  setApplicationSubmitted,
  setCvUploaded,
  setFavorites,
  setListOfProgramsAsked,
  setMeetingScheduled,
  setPreferencesFilled,
  setSchoolShortlisted,
  setTestsScoresUploaded,
} from 'features/user/userSlice';

import styles from './Dashboard.module.scss';
import NextStepsCard from '../../../cards/NextStepsCard';
import { UserState } from '../../../userSlice';
import {
  useGetFavoritesQuery,
  useGetNextStepsQuery,
  useGetBackofficeUserQuery,
} from 'services/exploreApi';
import Loader from 'common/components/Loader/Loader';
import ArticleCard from './ArticleCard';

const articlesInformation = [
  {
    title: "When Is The Best Time to Start a Master's Degree Program",
    date: 'Jul 20, 2022',
    link: 'https://blog.unifyapply.com/best-time-to-start-a-masters-degree/',
    time: 3,
  },
  {
    title: 'Picking the Right Business School Location: 6 Considerations to Make',
    date: 'Aug 31, 2022',
    link: 'https://blog.unifyapply.com/picking-the-right-business-school-location-6-considerations-to-make/',
    time: 3,
  },
  {
    title: 'Why Europe is the Best Destination for Business Studies?',
    date: 'Dec 6, 2022',
    link: 'https://blog.unifyapply.com/why-europe-is-the-best-destination-for-business-studies/',
    time: 9,
  },
];

const UserDashboard: FunctionComponent = () => {
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { user, profile, logged } = useAppSelector(
    ({ session }: { session: SessionState }) => session
  );
  const { data: userData } = useGetBackofficeUserQuery(id || '', {
    skip: !id || (!!user && String(user?.id) !== id),
  });
  const dispatch = useAppDispatch();
  const [isBackoffice, setIsBackoffice] = useState<boolean>(
    location.pathname.includes('backoffice')
  );
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const { data: nextSteps, isLoading } = useGetNextStepsQuery(undefined, { skip: !logged });
  const { data: favoritesPrograms } = useGetFavoritesQuery('Program', { skip: !logged });

  useEffect(() => {
    if (location.pathname.includes('backoffice')) {
      setIsBackoffice(true);
    }
  }, [location]);

  useEffect(() => {
    if (logged) {
      dispatch(setFavorites(favoritesPrograms?.data.map((item) => item.id)));
    }
  }, []);

  useEffect(() => {
    if (location.pathname.includes('backoffice') && id) {
      const newProfile = userData?.data.profile;
      if (userData?.data) {
        dispatch(setUser(userData.data));
        dispatch(setProfile(newProfile));
      }
    }
  }, [userData]);

  useEffect(() => {
    if (profile) dispatch(setPreferencesFilled(true));
    if (nextSteps) {
      dispatch(setSchoolShortlisted(nextSteps.data.school_shortlisted));
      dispatch(setMeetingScheduled(nextSteps.data.meeting_scheduled));
      dispatch(setListOfProgramsAsked(nextSteps.data.list_of_programs_asked));
      dispatch(setApplicationStarted(nextSteps.data.application_started));
      dispatch(setCvUploaded(nextSteps.data.cv_uploaded));
      dispatch(setTestsScoresUploaded(nextSteps.data.tests_scores_uploaded));
      dispatch(setApplicationSubmitted(nextSteps.data.application_submitted));
    }
    dispatch(setSessionChecked());
  }, [nextSteps]);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <div className={isMobile ? '' : `${styles.desktopContainer} row pe-3`}>
        <div className={isMobile ? '' : `${styles.leftColumn} col-lg-12 col-xl-9`}>
          <div className={styles.dashboardContainer}>
            <Text size={isMobile ? 20 : 36} weight={700}>
              Welcome back 👋, {user?.name ? user.name.split(' ')[0] : ''}
            </Text>
            {isLoading ? <Loader /> : !isBackoffice && <NextStepsCard />}
            <div className="d-xxl-none d-block mt-4">
              <ProfileCard />
            </div>
            <div className={styles.topicContainer}>{<ExploreTopic />}</div>
          </div>
        </div>
        {!isMobile && (
          <div className={`${styles.rightColumn} col-lg-12 col-xl-3 pe-0`}>
            <div className="d-none d-xxl-block">
              <h4 className="fw-600 mb-3">My Profile</h4>
              <ProfileCard />
            </div>
            <h6 className="fw-600 mt-5 mb-2">Articles</h6>
            <div className="d-flex flex-column gap-3">
              {articlesInformation.map((article) => (
                <ArticleCard
                  key={article.title}
                  title={article.title}
                  link={article.link}
                  date={article.date}
                  time={article.time}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default UserDashboard;
